import { useEffect, useState } from 'react'
import type { Partner } from '@shared/types'
import {
  createQuoteFromTemplate, deleteTemplate, discardTemplateWorkspace, fetchTemplates,
  openTemplateWorkspace, patchTemplate,
  type SessionRole, type TemplateRow,
} from '../lib/api.js'

/**
 * Quote templates (decisions 17 + 96) — Mark's call: a dedicated page (over
 * the picker-only option), visible to every signed-in user.
 *
 * Wave9-global templates are visible to all partners and managed by any Wave9
 * internal user; partner templates belong to the acting partner. Saving
 * happens in the BUILDER ("Save as template" on any quote with lines); this
 * page starts quotes from them and manages the library.
 *
 * The decision-17 rule, front and centre: templates carry structure and
 * product ids — catalogue prices are ALWAYS today's, for the using partner's
 * mode and tier. Ad-hoc lines carry saved prices and get flagged for review.
 */

export function Templates({ partner, role, onOpen, onEdit }: {
  partner: Partner | null
  role: SessionRole | null
  onOpen: (quoteId: string) => void
  /** Decision 125: hand off to the template editor (workspace already open). */
  onEdit: (templateId: string, quoteId: string, resumed: boolean) => void
}) {
  const [rows, setRows] = useState<TemplateRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [renameTo, setRenameTo] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const load = () => {
    fetchTemplates().then((r) => setRows(r.templates)).catch((e: Error) => setError(e.message))
  }
  useEffect(load, [])

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    setBusy(true)
    try { await fn(); load() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  if (error && !rows) return <p className="pill pill--stop">{error}</p>
  if (!rows) return <p className="muted">Loading…</p>

  const globals = rows.filter((t) => t.partnerId === null)
  const own = rows.filter((t) => t.partnerId !== null)

  /**
   * Decision 125 (supersedes #122's editing mechanism): open the real editor.
   * No draft is created, no quote number is spent — the workspace row is
   * invisible everywhere else and disappears on save or discard.
   */
  const edit = (t: TemplateRow) => run(async () => {
    const ws = await openTemplateWorkspace(t.id)
    onEdit(t.id, ws.quoteId, ws.resumed)
  })

  const startFrom = (t: TemplateRow, editing = false) => run(async () => {
    const res = await createQuoteFromTemplate(t.id)
    const notes: string[] = []
    if (res.skipped.length > 0) notes.push(`skipped ${res.skipped.length} line(s) — inactive, or not available in this partner's mode: ${res.skipped.join(', ')}`)
    if (res.adhocCarried.length > 0) notes.push(`review the ad-hoc price(s) on: ${res.adhocCarried.join(', ')}`)
    // Decision 124: a retired terms set no longer vanishes in silence — the
    // quote has NO T&Cs and that must be said before anybody sends it.
    if (res.retiredTerms) notes.push(`this template's T&Cs (${res.retiredTerms}) are retired — the quote has none, pick a set in the Send panel`)
    setNotice(editing
      ? `Opened ${res.quoteNumber} as an editing draft of “${res.templateName}” — make your changes, then “Save as template” → “Update: ${res.templateName}”. Delete the draft after if you don't need it.`
      : `Created ${res.quoteNumber} from “${res.templateName}” — repriced from today's catalogue${notes.length > 0 ? '. ⚠ ' + notes.join(' · ') : '.'}`)
    onOpen(res.id)
  })

  const templateRow = (t: TemplateRow) => (
    <div key={t.id} className="school" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <strong>{t.name}</strong>{' '}
          {t.partnerId === null && <span className="pill pill--brand">Wave9 global</span>}{' '}
          {!t.active && <span className="pill pill--stop">retired</span>}{' '}
          {t.adhocLines > 0 && <span className="pill pill--warn" title="Ad-hoc lines carry the prices saved in the template — review them on each new quote.">{t.adhocLines} ad-hoc</span>}{' '}
          {t.editing && <span className="pill pill--warn" title="Someone has an editing session open on this template. Its saved version is unchanged until they save.">unsaved edit</span>}
          <div className="small muted">
            {t.summary} · saved {new Date(t.createdAt).toLocaleDateString('en-GB')}
          </div>
          {/* Decision 124: say what a quote made from this arrives with — the
              cover note, footer and T&Cs have always ridden the payload, but
              nothing showed them, so nobody could tell an empty one. */}
          <div className="tiny muted" title="These ride along to every quote made from this template.">
            {t.carries.label}
          </div>
        </div>
        <button className="btn btn--sm btn--primary" disabled={busy || !partner || !t.active}
          title={partner ? 'Creates a draft with this structure, repriced from today\'s catalogue for the acting partner — then pick the customer as usual.' : 'Choose a partner under "Acting as" first.'}
          onClick={() => startFrom(t)}>New quote →</button>
        {t.canManage && (
          <>
            <button className="btn btn--sm" disabled={busy || !partner}
              title={t.editing
                ? 'Continue the unsaved edit on this template.'
                : 'Open this template in the template editor — change its sections, lines, cover note, footer and T&Cs. No quote is created.'}
              onClick={() => edit(t)}>{t.editing ? 'Continue editing…' : 'Edit…'}</button>
            {t.editing && (
              <button className="btn btn--sm btn--danger" disabled={busy}
                title="Throw the unsaved changes away — the template stays as last saved."
                onClick={() => {
                  if (!window.confirm(`Discard the unsaved changes to “${t.name}”?`)) return
                  void run(() => discardTemplateWorkspace(t.id))
                }}>Discard edit</button>
            )}
            <button className="btn btn--sm" disabled={busy} onClick={() => {
              if (renaming === t.id) { setRenaming(null); return }
              setRenaming(t.id); setRenameTo(t.name)
            }}>{renaming === t.id ? 'Cancel' : 'Rename'}</button>
            <button className="btn btn--sm" disabled={busy}
              title="Retired templates stay here (for reinstating) but leave the pick list."
              onClick={() => run(() => patchTemplate(t.id, { active: !t.active }))}>
              {t.active ? 'Retire' : 'Reinstate'}
            </button>
            <button className="btn btn--sm btn--danger" disabled={busy}
              title="Templates are copied into quotes at use — deleting one never touches existing quotes."
              onClick={() => {
                if (!window.confirm(`Delete template “${t.name}” completely? Existing quotes are unaffected.`)) return
                void run(() => deleteTemplate(t.id))
              }}>Delete</button>
          </>
        )}
      </div>
      {renaming === t.id && (
        <div className="rowflex" style={{ marginTop: 8 }}>
          <input className="input" style={{ flex: 1 }} value={renameTo} onChange={(e) => setRenameTo(e.target.value)} />
          <button className="btn btn--sm btn--primary" disabled={busy || !renameTo.trim()}
            onClick={() => run(async () => { await patchTemplate(t.id, { name: renameTo.trim() }); setRenaming(null) })}>Save</button>
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Quote templates</h1>
          <p>
            Reusable quote structures: sections, option blocks, lines, cover note, terms and
            attachments — <strong>never prices</strong>. Every new quote reprices from today's catalogue for the
            acting partner; ad-hoc lines carry their saved prices and are flagged for review. Save one from any
            quote via “Save as template” in the builder. To change an existing one, use <strong>Edit…</strong>
            below — that opens the <strong>template editor</strong>, where the sections, lines, cover note,
            footer and T&amp;Cs are all editable in one place. Editing a template never creates a quote or
            spends a quote number. A template's name becomes the new quote's starting title.
          </p>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't work.</strong><span>{error}</span></div>}
      {notice && <div className="banner">✓ <span>{notice}</span></div>}
      {!partner && (
        <div className="banner">
          ℹ <span>Choose a partner under “Acting as” to start a quote from a template — the library is browsable meanwhile.</span>
        </div>
      )}

      <div className="card">
        <div className="card__head">
          <h2>Wave9 templates — all partners see these</h2>
          <span className="muted small">Managed by Wave9 staff (staff-wide, like the asset library).</span>
        </div>
        <div className="card__body">
          {globals.length === 0 && (
            <p className="muted small">
              None yet. Open any quote with lines and use “Save as template” — {role !== null ? 'tick “Wave9-global” to share it with every partner.' : 'Wave9 staff can share templates with every partner.'}
            </p>
          )}
          {globals.map(templateRow)}
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2>{partner ? `${partner.name} templates` : 'Partner templates'}</h2>
          <span className="muted small">Visible inside this partner account only — partner isolation as ever.</span>
        </div>
        <div className="card__body">
          {own.length === 0 && (
            <p className="muted small">
              {partner
                ? 'None yet for this partner. “Save as template” in the builder (without the global tick) files it here.'
                : 'Choose a partner under “Acting as” to see their templates.'}
            </p>
          )}
          {own.map(templateRow)}
        </div>
      </div>
    </>
  )
}
